import React from 'react';
import PropTypes from 'prop-types';

import DisplayRichContent from '../../../global/components/forms/DisplayRichContent';
import ContentModuleCard from '../../contentModule/components/ContentModuleCard';

import { checkIfRichText } from '../../../global/utils/richContentUtils';
import LandingTemplateFooter from './LandingTemplateFooter';
import StyledButton from 'shared-resources/components/src/StyledButton';

// proof of concept for a public landing page template
const PublicLandingTemplate1 = ({ templateConfig, contentModules = [], handleOpenModule, handleLoginClick, handleSignupClick, isLoggedIn, groupRegistrationAccess, isGroupMember }) => {
  const {
    logoUrl1
    , heroUrl1
    , header1
    , subHeader1
    , body1
    , body2
    , embedContent
    , footer
    , footerLogoUrl
    , footerCaption
    , persistentFooter
    , persistentFooterLogoUrl
    , displayModuleDescriptions
    , showPfLogo
    , allowLogin
    , allowSignup
    , externalCallToActionUrl
    , externalCallToActionLabel
  } = templateConfig || {};

  const defaultTextClass = '!text-pf-black-800';

  return (
    <div className={`h-full min-h-fit mx-auto bg-pf-white rounded-sm`}>
      {/* Header Section */}
      <header className="flex py-4 justify-between items-center border-gray-300" id='header-top'>
        {logoUrl1 ?
          <div className="">
            <img src={logoUrl1} alt="Client Logo" className="h-8 w-auto" />
          </div>
          :
          <div className="">
            <img src={"https://pro-ficiency-static.s3.us-east-2.amazonaws.com/atlas/slp_full_blue.png"} alt="Client Logo" className="h-8 w-auto" />
          </div>
        }
        {/* 
          * not sure if we always want to show View Dashboard button if logged in regardless of group membership
          * if allowLogin && isLoggedIn && isGroupMember => 'View Dashboard' button
          * if allowLogin && allowSignup&& isLoggedIn && !isGroupMember => 'Join Group' button
          * if allowLogin && !isLoggedIn => 'Login' button
          * if allowLogin && allowSignup && !isLoggedIn => 'Sign Up' button
          */}
        {allowLogin && (
          <div className="flex gap-4">
            {!isLoggedIn ? 
              <div className="flex gap-4">
                <StyledButton
                  variant='primary'
                  size='sm'
                  onClick={handleLoginClick}
                >
                  {'Login'}
                </StyledButton>
                {allowSignup && groupRegistrationAccess && (
                  <StyledButton
                    variant='tertiary'
                    size='sm'
                    onClick={handleSignupClick}
                  >
                    Sign Up
                  </StyledButton>
                )}
              </div>
            : isLoggedIn && isGroupMember ? // should View Dashboard button always show if logged in? reglardless of group membership? 
              <StyledButton
                variant='primary'
                size='sm'
                onClick={handleLoginClick}
              >
                {'View Dashboard'} 
              </StyledButton>
            : isLoggedIn && !isGroupMember && allowSignup && groupRegistrationAccess ?
              <StyledButton
                variant='tertiary'
                size='sm'
                onClick={handleSignupClick}
              >
                Join Group
              </StyledButton>
            : null 
            }
            {externalCallToActionUrl && externalCallToActionLabel ? (
              <StyledButton
                variant='secondary'
                size='sm'
                href={externalCallToActionUrl}
              >
                {externalCallToActionLabel}
              </StyledButton>
            ) : null}
          </div>
        )}
      </header>
      {/* Main Content */}
      <div className={`${defaultTextClass} flex flex-col min-h-screen gap-8 md:gap-14`} id="main-content">
        {/* Hero Section */}
        {(heroUrl1 || header1 || body1 || subHeader1) && (
          <section className="flex flex-col gap-8 md:flex-row md:gap-10 -mx-4 sm:-mx-20 bg-gray-100">
            {(header1 || subHeader1) && (
              <div className={`flex flex-col justify-center h-full w-full my-auto px-8 md:mx-12 ${heroUrl1 ? 'md:w-1/2' : 'py-8'}`}> {/* padding & margin on text to line up with rest of the page */}
                {header1 && <h2 className="!text-inherit md:text-3xl font-semibold text-center md:text-left">{header1}</h2>}
                {subHeader1 && <p className="!text-inherit text-base font-semibold text-center md:text-left">{subHeader1}</p>}
                {externalCallToActionLabel && externalCallToActionUrl && (
                  <div className="mt-4 text-center md:text-left md:mb-6">
                    <StyledButton
                      variant='primary'
                      size='sm'
                      href={externalCallToActionUrl}
                    >
                      {externalCallToActionLabel}
                    </StyledButton>
                  </div>
                )}
              </div>
            )}
            {heroUrl1 && (
              <div className={`w-full ${header1 || subHeader1 ? 'md:w-1/2' : ''}`}>
                <img src={heroUrl1} alt="Hero Image" className="object-cover w-full h-full max-h-[800px]" />
              </div>
            )}
          </section>
        )}

        {/* Body1 Section */}
        {checkIfRichText(body1) &&
          <DisplayRichContent className="border-none" content={body1} />
        }

        {/* Embed Content Section */}
        {embedContent && contentModules?.length > 0 && (
          <section className="flex flex-col gap-4 align-top justify-center sm:flex-wrap sm:flex-row sm:gap-8">
            {contentModules.map((cm) => (
              <ContentModuleCard
                key={cm?._id}
                contentModule={cm}
                handleOpenModule={() => { handleOpenModule(cm._id) }}
                displayModuleDescriptions={displayModuleDescriptions}
              />
            ))}
          </section>
        )}

        {/* Body2 Section */}
        {checkIfRichText(body2) &&
          <div className="px-4 md:px-20 py-6 bg-gray-100 -mx-4 sm:-mx-20">
            <DisplayRichContent className="border-none" content={body2} />
          </div>
        }
      </div>

      {/* Footer Section */}
      <LandingTemplateFooter
        footer={footer}
        footerLogoUrl={footerLogoUrl}
        footerCaption={footerCaption}
        persistentFooter={persistentFooter}
        persistentFooterLogoUrl={persistentFooterLogoUrl}
        showPfLogo={showPfLogo}
      />
    </div>
  );
};

PublicLandingTemplate1.propTypes = {
  templateConfig: PropTypes.shape({
    logoUrl1: PropTypes.string
    , heroUrl1: PropTypes.string
    , header1: PropTypes.string
    , subHeader1: PropTypes.string
    , body1: PropTypes.object
    , embedContent: PropTypes.bool
    , footer1: PropTypes.object
    , allowLogin: PropTypes.bool
  })
  , searchParams: PropTypes.object
  , moduleLinks: PropTypes.array
  , contentModules: PropTypes.array
  , handleOpenModule: PropTypes.func
};



export default PublicLandingTemplate1;